import image from 'images/devices/keon2-promotion.png';

import Stroker from './Stroker';
import * as DeviceStates from './DeviceStates';
import { CONNECTION_WAY } from 'data/constants';
import { toFullUUID } from 'utils/bluetooth-uuid-helper';
import { tranformDataToArray } from 'utils/helpers';
import { wait } from 'utils/wait';

const PRIMARY_SERVICE_UUID: BluetoothCharacteristicUUID = 0x1400; // SERVICE_UUID in RN impl
const BATTERY_STATUS_CHAR_UUID: BluetoothCharacteristicUUID = 0x2a19; // INFO_BATTERY_UUID
const MOTOR_COMMAND_CHAR_UUID: BluetoothCharacteristicUUID = 0x1801; // MOTOR_UUID
const WIFI_PROV_CONTROL_UUID: BluetoothCharacteristicUUID = 0x1901; // WIFI_PROV_CONTROL_UUID
const SERIAL_NUMBER_CHAR_UUID: BluetoothCharacteristicUUID = 0x2a25; // SERIAL_NUMBER_UUID

const names = ['KEON2'];

const KEON_MIN_POS: number = 0;
const KEON_MAX_POS: number = 99;
// Matches KEON_SPEED_RATIO from RN Keon2 implementation
const KEON_FULL_CYCLE_TIME_MSEC: number = 250;

const DEFAULT_MTU_SIZE: number = 23;
const TOKEN_PACKET_MAX_SIZE: number = 500;

const PREFIX_SSID_NAME: string = '0';
const PREFIX_PASSWORD: string = '1';
const PREFIX_TOKEN: string = '2';

const PROV_CHANGE_MODE_WIFI: number = 0x1;
const PROV_START: number = 0x2;
const PROV_CRED_CONFIRM: number = 0x3;
const PROV_CLEAN_LIST: number = 0x5;

type ConnectionMode = (typeof CONNECTION_WAY)[keyof typeof CONNECTION_WAY];

interface DeviceStatusData {
  sid: string;
  bleAddress: string;
  serialNumber: string;
  batteryStatus: number;
  encoderPos: number;
  motorSpeed: number;
  wifiStrength: number;
  intensity: number;
  firmwareVersion: string;
  strokerLevel: number;
  statusPeriod: number;
  errorCode: number;
}

const initialDeviceStatusData: DeviceStatusData = {
  sid: '',
  bleAddress: '',
  serialNumber: '',
  batteryStatus: 0,
  encoderPos: 0,
  motorSpeed: 0,
  wifiStrength: 0,
  intensity: 0,
  firmwareVersion: '',
  strokerLevel: 0,
  statusPeriod: 0,
  errorCode: 0,
};

export default class Keon2 extends Stroker {
  supportProvisioning: boolean;
  connectionMode: ConnectionMode;
  fnSendMessageToDevices: (eventName: string, args: any) => void;
  deviceStatusData: DeviceStatusData = initialDeviceStatusData;

  constructor(device: BluetoothDevice) {
    super(
      device,
      toFullUUID(PRIMARY_SERVICE_UUID),
      toFullUUID(MOTOR_COMMAND_CHAR_UUID),
      image,
      toFullUUID(BATTERY_STATUS_CHAR_UUID),
      KEON_MAX_POS,
      KEON_MIN_POS,
      KEON_FULL_CYCLE_TIME_MSEC,
    );

    this.supportProvisioning = true;
    this.connectionMode = CONNECTION_WAY.BT;
  }

  static get deviceNames() {
    return names;
  }

  static get services() {
    return [toFullUUID(PRIMARY_SERVICE_UUID)];
  }

  get companyName() {
    return 'Kiiroo';
  }

  getConnectionMode = (): ConnectionMode => {
    return this.connectionMode;
  };

  sendCommandToDevices = (commandType: string, args: any) => {
    this.fnSendMessageToDevices?.('send_command_to_devices', {
      command_type: commandType,
      arguments: args,
    });
  };

  sendSetupToDevice = (setupType: string, args: any) => {
    this.fnSendMessageToDevices?.('send_settings_to_device', {
      setup_type: setupType,
      arguments: args,
    });
  };

  sendReprovisionCommandToDevice = (reprovisionType: string, args: any = null) => {
    this.fnSendMessageToDevices?.('send_reprovision_command_to_device', {
      reprovision_type: reprovisionType,
      arguments: args,
    });
  };

  async connect(isConnectionByWifi) {
    if (isConnectionByWifi) {
      this.setConnectionState(DeviceStates.CONNECTED);
      this.connectionMode = CONNECTION_WAY.WIFI;
    } else {
      this.connectionMode = CONNECTION_WAY.BT;
      await super.connect();

      // Identifies this exact toy even after the browser has reset its device id
      await this.readSerialNumber(SERIAL_NUMBER_CHAR_UUID);

      // Move Keon2 to the movement mode, same command format as RN Keon2
      await this.motorChar.writeValue(tranformDataToArray([0x00]));

      await this.motorChar.startNotifications();
      this.motorChar.addEventListener('characteristicvaluechanged', (event) => {
        const valueArray = tranformDataToArray(event.currentTarget.value.buffer);
        const pos = 100 - valueArray[1];
        this.deviceMovementCallback({ position: pos, speed: 0 });
      });
    }
  }

  async disconnect(removeDevice: boolean = false) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await super.disconnect(removeDevice);
    } else {
      this.connectionMode = CONNECTION_WAY.BT;
      this.setConnectionState(DeviceStates.DISCONNECTED);
    }
  }

  async moveTo(position: number, speed: number, isOntimer?: boolean): Promise<void> {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      // Delegate to base implementation which uses this.writeValue under the hood
      await super.moveTo(position, speed);
    } else {
      if (isOntimer) {
        const pos = Math.max(this.minPosition, Math.min(this.maxPosition, Math.round(position)));
        const spd = Math.max(0, Math.min(99, Math.round(speed * 0.5)));
        this.sendCommandToDevices('MOVEMENT', {
          speed: spd,
          position: pos,
        });
      }
    }
  }

  async writeValue(position: number, speed: number) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      // Same command format as RN Keon2: [0x03, 0x01, speed, position]
      await this.motorChar.writeValue(tranformDataToArray([0x03, 0x01, speed, position]));
    }
  }

  async writePauseValue() {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      // Stop command: [0x00]
      await this.motorChar.writeValue(tranformDataToArray([0x00]));
    } else {
      this.sendCommandToDevices('PAUSE', {});
    }
  }

  async setMaxIntensity(percent: number) {
    super.setMaxIntensity(percent);
    if (this.connectionMode === CONNECTION_WAY.WIFI) {
      const speedIntensity = Math.min(6, Math.floor((percent / 100) * 5) + 1);
      const rangeIntensity = Math.min(4, Math.floor((percent / 100) * 3) + 1);
      this.sendSetupToDevice('speed_intensity_adjustment', { intensity: speedIntensity });
      this.sendSetupToDevice('range_intensity_adjustment', { intensity: rangeIntensity });
      this.sendSetupToDevice('status_update_interval', { interval: 100 });
    }
  }

  async writeMovementBetween(minPosition: number, maxPosition: number, speed: number) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await this.motorChar.writeValue(
        tranformDataToArray([0x04, 0x00, speed, minPosition, maxPosition]),
      );
    } else {
      this.sendCommandToDevices('MOVEMENT_BETWEEN', {
        speed,
        min_position: minPosition,
        max_position: maxPosition,
      });
    }
  }

  async getBattery() {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      try {
        const readData = await this.statusChar.readValue();
        if (readData) {
          return readData.getUint8(0);
        }
      } catch (e) {
        console.error('Keon2.getBattery:', e);
      }
      return -1;
    } else {
      return this.deviceStatusData.batteryStatus;
    }
  }

  isConnected(): boolean {
    return !!this.device?.gatt?.connected;
  }

  async testDevice() {
    console.log('Keon2.testDevice', this.connectionMode);
    if (this.connectionMode === CONNECTION_WAY.BT) {
      return await super.testDevice();
    } else {
      this.sendCommandToDevices('MOVEMENT_BETWEEN', {
        // WiFi motor speed is 0..100 (see firmware doc 8.5). Use a
        // moderate speed and mid-range stroke for a gentle test.
        speed: 20,
        min_position: 20,
        max_position: 80,
      });
      await wait(5000);

      this.sendCommandToDevices('PAUSE', {});
    }
  }

  async provisioning(ssid: string, psswrd: string, token: string): Promise<void> {
    if (!this.sensorService || !this.isConnected()) {
      return Promise.reject('The device should be connected first.');
    }
    if (!ssid || !psswrd || !token) {
      return Promise.reject('The credentials are invalid or not found');
    }

    const service = this.sensorService;
    const provChar: BluetoothRemoteGATTCharacteristic =
      await service.getCharacteristic(WIFI_PROV_CONTROL_UUID);

    // delete all saved user information related with Wi-Fi
    await provChar.writeValue(tranformDataToArray([PROV_CLEAN_LIST]));
    await wait(100);

    // Change current BLE mode to Wi-Fi by software
    await provChar.writeValue(tranformDataToArray([PROV_CHANGE_MODE_WIFI]));
    await wait(100);

    // Inform device to prepare necessary buffers for provisioning
    await provChar.writeValue(tranformDataToArray([PROV_START]));
    await wait(100);

    // Send SSID
    await provChar.writeValue(tranformDataToArray(PREFIX_SSID_NAME + ssid));
    await wait(100);

    // Send wifi password
    await provChar.writeValue(tranformDataToArray(PREFIX_PASSWORD + psswrd));
    await wait(100);

    // Token in chunks (size derived from DEFAULT_MTU_SIZE like RN impl)
    const packetSize = DEFAULT_MTU_SIZE - 4;
    for (let i = 0; i < token.length; i += packetSize) {
      const chunk = token.slice(i, i + packetSize);
      await provChar.writeValue(tranformDataToArray(PREFIX_TOKEN + chunk));
      await wait(100);
    }

    // Confirm credentials
    await provChar.writeValue(tranformDataToArray([PROV_CRED_CONFIRM]));
    console.log('Keon2.provisioning: PROV_CRED_CONFIRM sent');
  }

  async resetProvisioningData(): Promise<void> {
    if (!this.sensorService || !this.isConnected()) {
      return Promise.reject('The device should be connected first.');
    }

    const service = this.sensorService;
    const provChar: BluetoothRemoteGATTCharacteristic =
      await service.getCharacteristic(WIFI_PROV_CONTROL_UUID);

    // As per firmware doc 8.2: send unsigned int 5 to reset Wi-Fi credentials.
    await provChar.writeValue(tranformDataToArray([PROV_CLEAN_LIST]));
    await wait(100);
  }

  switchToBtMode = () => {
    this.sendReprovisionCommandToDevice('bt_mode', {});
    this.setConnectionState(DeviceStates.DISCONNECTED);
    this.connectionMode = CONNECTION_WAY.BT;
  };

  resetDeviceStatus = () => {
    this.sendReprovisionCommandToDevice('reset_credentials', {});
    this.deviceStatusData = initialDeviceStatusData;
    this.setConnectionState(DeviceStates.DISCONNECTED);
    this.connectionMode = CONNECTION_WAY.BT;
  };

  setServerChanels(sendMessageToDevices: (eventName: string, args: any) => void): void {
    this.fnSendMessageToDevices = sendMessageToDevices;
  }

  refreshStatus(sid: string, deviceInfo: any): void {
    this.deviceStatusData.sid = sid;
    this.deviceStatusData.bleAddress = deviceInfo?.ble_address;
    this.deviceStatusData.serialNumber = deviceInfo?.serial_number;
    this.deviceStatusData.batteryStatus = deviceInfo?.battery_status;
    this.deviceStatusData.encoderPos = deviceInfo?.encoder_position;
    this.deviceStatusData.motorSpeed = deviceInfo?.motor_speed;
    this.deviceStatusData.wifiStrength = deviceInfo?.wifi_strength;
    this.deviceStatusData.intensity = deviceInfo?.intensity;
    this.deviceStatusData.strokerLevel = deviceInfo?.stroker_level;
    this.deviceStatusData.statusPeriod = deviceInfo?.status_period;
    this.deviceStatusData.errorCode = deviceInfo?.error_code;
    this.deviceStatusData.firmwareVersion = deviceInfo?.firmware_version;
  }

  public getStatus(): DeviceStatusData {
    return this.deviceStatusData;
  }

  async onTimer() {
    const AMBIENT_MOVEMENT_MIN_THRESHOLD = 3;

    const percent = +this.ambientMovementMixin.ambientIntensity;
    if (percent < AMBIENT_MOVEMENT_MIN_THRESHOLD) {
      return;
    }

    if (performance.now() < this.ambientMovementMixin.ambientNextMovementTimestamp) {
      return;
    }

    // Auto Control (ambientIntensity) drives speed for both WiFi and BT.
    // Stroke range is handled externally: firmware via setMaxIntensity for WiFi, Power Control for BT.
    // BT: halve the speed to compensate for Keon2's faster motor; WiFi moveTo already applies ×0.5.
    const isWifi = this.connectionMode === CONNECTION_WAY.WIFI;
    const speed = isWifi ? percent : percent * 0.5;
    const newPos = 100 - this.ambientMovementMixin.currentPosition;
    await this.moveTo(newPos, speed, true);
    this.ambientMovementMixin.currentPosition = newPos;

    this.ambientMovementMixin.ambientNextMovementTimestamp =
      performance.now() + (20000 / percent) * 1.5;
  }
}
